/**
 * ✅ Headcount Verification Modal (admin only)
 * يقارن ملف إكسل بأكواد الموظفين (Client Code) بقاعدة البيانات ويُظهر كل الفروق
 *
 * Flow:
 *   1. choose scope  → one working company, or the whole client
 *   2. pick the Excel → POST /employees/headcount/verify (multipart)
 *   3. results       → banner + category chips + highlighted table + .xlsx report
 *   4. actions       → fix the differences without leaving the modal:
 *        Activate · Restore · Apply file values · Move to company
 *          → POST /employees/headcount/apply
 *        Resign / Terminate (payroll-aware, same as the Changes page)
 *          → POST /employee-actions/bulk/validate → /commit → poll /batches/:id
 *        Edit · View · Add → the page's own modals (onEditEmployee / onViewEmployee / onAddEmployee)
 *      After every action the same file is verified again automatically.
 *
 * The server does all the comparing and every write; this component only
 * uploads, renders and asks.
 * See HEADCOUNT-VERIFICATION-REPORT.md (user-service root).
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx-js-style";
import {
  X, Upload, ShieldCheck, Download, RotateCcw, Search, FileSpreadsheet,
  UserCheck, ArchiveRestore, Wand2, Building2, UserMinus, Pencil, Eye, UserPlus, Loader2,
} from "lucide-react";
import { apiFetch } from "../../../../context/AuthContext";
import bulkTranslations, { translateBulkMessage } from "../../../(Admin)EmployeeActions/Constant/EmployeeActions.bulk.i18n";

const BASE_URL = process.env.REACT_APP_BASE_URL;
const PAGE = 300; // rows rendered at a time (big clients can have thousands)

// Category → label, colour, sort order. Red = in the file but NOT active,
// amber = active but NOT in the file, blue = active with a data difference.
const CATS = {
  INACTIVE_IN_SYSTEM: { en: "Not active in system", ar: "غير نشط في النظام", color: "#dc2626", fill: "FEE2E2" },
  ARCHIVED:           { en: "Archived (resigned)", ar: "مؤرشف (مستقيل)", color: "#dc2626", fill: "FEE2E2" },
  NOT_FOUND:          { en: "Not found in system", ar: "غير موجود في النظام", color: "#dc2626", fill: "FEE2E2" },
  OTHER_COMPANY:      { en: "Belongs to another company", ar: "تابع لشركة أخرى", color: "#c2410c", fill: "FFEDD5" },
  MISSING_FROM_FILE:  { en: "Active but missing from file", ar: "نشط لكنه غير موجود بالملف", color: "#d97706", fill: "FEF3C7" },
  DATA_MISMATCH:      { en: "Data differs", ar: "بيانات مختلفة", color: "#2563eb", fill: "DBEAFE" },
  ON_LEAVE:           { en: "On leave", ar: "في إجازة", color: "#7c3aed", fill: "EDE9FE" },
  DUPLICATE_IN_FILE:  { en: "Duplicated in file", ar: "مكرر في الملف", color: "#64748b", fill: "E2E8F0" },
  INVALID_ROW:        { en: "Row without client code", ar: "صف بدون كود", color: "#64748b", fill: "E2E8F0" },
  OK:                 { en: "Active & matching", ar: "نشط ومطابق", color: "#16a34a", fill: "DCFCE7" },
};
const ORDER = Object.keys(CATS);

const T = {
  en: {
    title: "Verify headcount",
    subtitle: "Upload the client's Excel list of client codes — every employee in it should be active. Every difference with the system is highlighted.",
    compareAgainst: "Compare against",
    company: "A specific company",
    wholeClient: "The whole client",
    chooseCompany: "Choose a company…",
    file: "Excel file",
    pick: "Choose file",
    hint: 'Required column: "Client Code". Optional, compared when present: Arabic Name, English Name, National ID, Working Company, Position, Department, Location, Hiring Date, Phone number, Email.',
    verify: "Verify",
    verifying: "Verifying…",
    cancel: "Cancel",
    allGood: (n) => `Headcount verified — all ${n} employees in the file are active and match the system.`,
    notGood: (n) => `Headcount does not match — ${n} difference(s) found.`,
    fileRows: "In file",
    activeInSystem: "Active in system",
    issues: "All differences",
    everything: "Everything",
    search: "Search code or name…",
    row: "Excel row", code: "Client code", name: "Name", companyCol: "Company", status: "System status", result: "Result", details: "Details",
    nothing: "Nothing to show for this filter.",
    showMore: "Show more",
    again: "Verify another file",
    download: "Download report (.xlsx)",
    system: "system", inFile: "file",
    scopeLabel: (s) => (s.workingCompany ? `Company: ${s.workingCompany}` : `Whole client: ${s.clientName}`),
    compared: "Fields compared",
    none: "client code & status only",
    actions: "Actions",
    act: {
      activate: "Activate", restore: "Restore", apply: "Apply file values", move: "Move to company",
      separate: "Resign / Terminate", edit: "Edit", view: "View", add: "Add employee",
    },
    selectedN: (n) => `${n} selected`,
    clear: "Clear",
    selectAll: "Select all eligible rows shown",
    confirm: "Confirm",
    working: "Working…",
    confirmTitle: {
      activate: (n) => `Set ${n} employee(s) to Active?`,
      restore: (n) => `Restore ${n} archived employee(s)?`,
      applyFileValues: (n) => `Overwrite system data with the file values for ${n} employee(s)?`,
      move: (n, c) => `Move ${n} employee(s) to "${c}"?`,
    },
    confirmBody: {
      activate: "Only the status changes to Active. Payroll and past separation actions are not touched — use the Changes page if a separation must be reversed formally.",
      restore: "They are moved back from the archive to the live employee list as Active (same as the Restore button in Archived).",
      applyFileValues: "Each changed field below is written exactly as in the file. Department differences can't be applied here — use Edit.",
      move: "Their Working Company is changed. Chat groups and visibility follow the new company.",
    },
    resultTitle: (ok, bad) => `${ok} done${bad ? `, ${bad} failed` : ""}`,
    reverifying: "Re-checking the file…",
    sep: {
      title: (n) => `Separate ${n} employee(s)`,
      hint: "Creates real Resignation / Termination actions through the Changes page engine: approvals, history and final-payroll proration apply. A future last working day is scheduled — the employee stays active until then.",
      type: "Action", resignation: "Resignation", terminate: "Termination",
      lwd: "Last working day", reason: "Reason", details: "Details", termType: "Termination type",
      check: "Check", submit: (n) => `Submit ${n}`, back: "Back", done: "Done",
      valid: "Ready", warning: "With warnings", error: "Will be skipped",
      running: "Creating actions…", finished: "Finished",
    },
    fileVal: "file value",
  },
  ar: {
    title: "التحقق من عدد الموظفين",
    subtitle: "ارفع ملف الإكسل الخاص بالعميل والذي يحتوي على أكواد الموظفين — من المفترض أن يكون كل من فيه نشطًا. سيتم تمييز كل فرق مع النظام.",
    compareAgainst: "المقارنة مع",
    company: "شركة محددة",
    wholeClient: "العميل بالكامل",
    chooseCompany: "اختر الشركة…",
    file: "ملف الإكسل",
    pick: "اختر الملف",
    hint: 'العمود المطلوب: "Client Code". أعمدة اختيارية تتم مقارنتها إن وُجدت: الاسم العربي، الاسم الإنجليزي، الرقم القومي، الشركة، الوظيفة، القسم، الموقع، تاريخ التعيين، رقم الهاتف، البريد الإلكتروني.',
    verify: "تحقق",
    verifying: "جارٍ التحقق…",
    cancel: "إلغاء",
    allGood: (n) => `تم التحقق — جميع الموظفين في الملف (${n}) نشطون ومطابقون للنظام.`,
    notGood: (n) => `العدد غير مطابق — تم العثور على ${n} فرق.`,
    fileRows: "في الملف",
    activeInSystem: "نشط في النظام",
    issues: "كل الفروق",
    everything: "الكل",
    search: "ابحث بالكود أو الاسم…",
    row: "صف الإكسل", code: "الكود", name: "الاسم", companyCol: "الشركة", status: "الحالة في النظام", result: "النتيجة", details: "التفاصيل",
    nothing: "لا توجد نتائج لهذا الفلتر.",
    showMore: "عرض المزيد",
    again: "تحقق من ملف آخر",
    download: "تحميل التقرير (.xlsx)",
    system: "النظام", inFile: "الملف",
    scopeLabel: (s) => (s.workingCompany ? `الشركة: ${s.workingCompany}` : `العميل بالكامل: ${s.clientName}`),
    compared: "الحقول التي تمت مقارنتها",
    none: "الكود والحالة فقط",
    actions: "الإجراءات",
    act: {
      activate: "تفعيل", restore: "استعادة", apply: "تطبيق قيم الملف", move: "نقل للشركة",
      separate: "استقالة / إنهاء خدمة", edit: "تعديل", view: "عرض", add: "إضافة موظف",
    },
    selectedN: (n) => `تم اختيار ${n}`,
    clear: "إلغاء الاختيار",
    selectAll: "اختيار كل الصفوف القابلة للإجراء المعروضة",
    confirm: "تأكيد",
    working: "جارٍ التنفيذ…",
    confirmTitle: {
      activate: (n) => `تفعيل ${n} موظف؟`,
      restore: (n) => `استعادة ${n} موظف من الأرشيف؟`,
      applyFileValues: (n) => `استبدال بيانات النظام بقيم الملف لعدد ${n} موظف؟`,
      move: (n, c) => `نقل ${n} موظف إلى "${c}"؟`,
    },
    confirmBody: {
      activate: "تتغير الحالة فقط إلى نشط. لا يتم تعديل الرواتب أو إجراءات إنهاء الخدمة السابقة — استخدم صفحة التغييرات لإلغاء إنهاء الخدمة رسميًا.",
      restore: "يتم نقلهم من الأرشيف إلى قائمة الموظفين كنشطين (مثل زر الاستعادة في الأرشيف).",
      applyFileValues: "يتم كتابة كل حقل مختلف كما هو في الملف. لا يمكن تطبيق فروق القسم هنا — استخدم التعديل.",
      move: "تتغير الشركة التي يعملون بها. مجموعات المحادثة والصلاحيات تتبع الشركة الجديدة.",
    },
    resultTitle: (ok, bad) => `تم تنفيذ ${ok}${bad ? `، وفشل ${bad}` : ""}`,
    reverifying: "جارٍ إعادة فحص الملف…",
    sep: {
      title: (n) => `إنهاء خدمة ${n} موظف`,
      hint: "ينشئ إجراءات استقالة / إنهاء خدمة حقيقية من خلال صفحة التغييرات: تطبق الموافقات والسجل وحساب الراتب الأخير. آخر يوم عمل في المستقبل يتم جدولته — ويبقى الموظف نشطًا حتى ذلك اليوم.",
      type: "الإجراء", resignation: "استقالة", terminate: "إنهاء خدمة",
      lwd: "آخر يوم عمل", reason: "السبب", details: "التفاصيل", termType: "نوع إنهاء الخدمة",
      check: "فحص", submit: (n) => `تنفيذ ${n}`, back: "رجوع", done: "تم",
      valid: "جاهز", warning: "مع تنبيهات", error: "سيتم تخطيه",
      running: "جارٍ إنشاء الإجراءات…", finished: "انتهى",
    },
    fileVal: "قيمة الملف",
  },
};

// Human sentence for one issue object returned by the server.
const issueText = (i, ar) => {
  switch (i.code) {
    case "MISSING_CLIENT_CODE": return ar ? "لا يوجد كود في هذا الصف" : "No client code in this row";
    case "DUPLICATE_OF_ROW": return ar ? `مكرر للصف ${i.row}` : `Duplicate of row ${i.row}`;
    case "FILE_CLIENT_DIFFERS": return ar ? `الملف يذكر العميل "${i.file}"` : `File says client "${i.file}"`;
    case "CODE_FORMAT_DIFFERS": return ar ? `الكود في الملف "${i.file}" وفي النظام "${i.system}"` : `Code written as "${i.file}" in file, "${i.system}" in system`;
    case "CODE_AMBIGUOUS": return ar ? `الكود يطابق أكثر من موظف: ${i.candidates.join("، ")}` : `Code matches several employees: ${i.candidates.join(", ")}`;
    case "ARCHIVED": return ar
      ? `مؤرشف${i.resignedDate ? ` — تاريخ الاستقالة ${i.resignedDate}` : ""}${i.reason ? ` (${i.reason})` : ""}`
      : `Archived${i.resignedDate ? ` — resigned ${i.resignedDate}` : ""}${i.reason ? ` (${i.reason})` : ""}`;
    case "NOT_FOUND": return ar ? "لا يوجد موظف بهذا الكود لدى هذا العميل" : "No employee with this code under this client";
    case "OTHER_COMPANY": return ar ? `يعمل في "${i.system || "—"}" وليس "${i.selected}"` : `Works at "${i.system || "—"}", not "${i.selected}"`;
    case "STATUS_NOT_ACTIVE": return ar ? `الحالة في النظام: ${i.status}` : `Status in system: ${i.status}`;
    case "STATUS_EMPTY": return ar ? "الحالة فارغة في النظام" : "Status is empty in the system";
    case "ON_LEAVE": return ar ? "في إجازة حاليًا" : "Currently on leave";
    case "MISSING_FROM_FILE": return ar ? "نشط في النظام لكنه غير مذكور في الملف" : "Active in the system but not in the file";
    case "MISSING_FROM_FILE_NO_CODE": return ar ? "نشط في النظام وليس له كود — لا يمكن مطابقته" : "Active in the system with NO client code — can't be matched";
    default: return i.code;
  }
};

// What can be done to a result row. `bulk` actions can run on a selection.
const rowActions = (r, selectedCompany) => {
  const has = !!r.infinityCode;
  const applicable = (r.diffs || []).filter((d) => d.applyKey);
  switch (r.category) {
    case "INACTIVE_IN_SYSTEM": return has ? ["activate", "edit"] : [];
    case "ARCHIVED": return has ? ["restore"] : [];
    case "MISSING_FROM_FILE": return has ? ["separate", "edit"] : [];
    case "OTHER_COMPANY": return has ? [...(selectedCompany ? ["move"] : []), "edit"] : [];
    case "DATA_MISMATCH": return has ? [...(applicable.length ? ["apply"] : []), "edit"] : [];
    case "NOT_FOUND": return ["add"];
    case "ON_LEAVE":
    case "OK": return has ? ["view"] : [];
    default: return [];
  }
};
const BULK = ["activate", "restore", "apply", "move", "separate"];
const ACT_ICON = {
  activate: UserCheck, restore: ArchiveRestore, apply: Wand2, move: Building2,
  separate: UserMinus, edit: Pencil, view: Eye, add: UserPlus,
};
const newKey = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID)
    ? crypto.randomUUID()
    : `hc-${Date.now()}-${Math.random().toString(36).slice(2)}`;
const todayIso = () => new Date().toISOString().slice(0, 10);

export default function HeadcountVerifyModal({
  isOpen,
  onClose,
  companyOptions = [],
  theme = "light",
  language = "en",
  hidden = false,            // parent hides us while its Edit/View/Add modal is open
  refreshSignal = 0,         // parent bumps this when that modal closes → re-verify
  onEditEmployee,            // (infinityCode) => void
  onViewEmployee,            // (infinityCode) => void
  onAddEmployee,             // () => void
}) {
  const ar = language === "ar";
  const t = T[ar ? "ar" : "en"];
  const bt = bulkTranslations[ar ? "ar" : "en"];
  const dark = theme === "dark";
  const fileRef = useRef(null);

  const [scope, setScope] = useState("company");
  const [company, setCompany] = useState("");
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("ISSUES");
  const [query, setQuery] = useState("");
  const [limit, setLimit] = useState(PAGE);

  // actions
  const [selected, setSelected] = useState(() => new Set()); // keys = result index
  const [confirm, setConfirm] = useState(null);   // { action, items, n, preview[] }
  const [busy, setBusy] = useState(false);
  const [lastResult, setLastResult] = useState(null); // { ok, bad, failures[] }
  const [sep, setSep] = useState(null);           // separation dialog state

  const c = {
    bg: dark ? "#0f172a" : "#ffffff",
    panel: dark ? "#1e293b" : "#f8fafc",
    border: dark ? "rgba(255,255,255,0.12)" : "rgba(15,23,42,0.12)",
    text: dark ? "#e2e8f0" : "#0f172a",
    dim: dark ? "#94a3b8" : "#64748b",
    accent: "#2e7cba",
  };

  const resetResult = () => {
    setData(null); setError(""); setFilter("ISSUES"); setQuery(""); setLimit(PAGE);
    setSelected(new Set()); setLastResult(null); setConfirm(null); setSep(null);
  };
  const resetAll = () => {
    resetResult(); setFile(null);
    if (fileRef.current) fileRef.current.value = "";
  };
  const close = () => { resetAll(); onClose?.(); };

  const verify = async ({ keepView = false } = {}) => {
    if (!file) return;
    setLoading(true); setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("scope", scope);
      if (scope === "company") fd.append("workingCompany", company);
      const res = await apiFetch(`${BASE_URL}/employees/headcount/verify`, { method: "POST", body: fd });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body?.success) {
        const msg = body?.messages?.[ar ? "ar" : "en"] || body?.message || body?.error;
        throw new Error(msg || (res.status === 403 ? (ar ? "غير مسموح" : "Access denied") : `HTTP ${res.status}`));
      }
      body.results = body.results.map((r, i) => ({ ...r, _key: i }));
      setData(body);
      setSelected(new Set());
      if (!keepView) setFilter(body.summary.allMatch ? "ALL" : "ISSUES");
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  };

  // Parent's Edit/View/Add modal closed → the data may have changed.
  const lastSignal = useRef(refreshSignal);
  useEffect(() => {
    if (refreshSignal !== lastSignal.current) {
      lastSignal.current = refreshSignal;
      if (data && file) verify({ keepView: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshSignal]);

  const counts = useMemo(() => {
    const m = {};
    (data?.results || []).forEach((r) => { m[r.category] = (m[r.category] || 0) + 1; });
    return m;
  }, [data]);

  const selectedCompany = data?.summary?.workingCompany || null;

  const visible = useMemo(() => {
    if (!data) return [];
    const q = query.trim().toLowerCase();
    return data.results
      .filter((r) =>
        filter === "ALL" ? true
          : filter === "ISSUES" ? r.category !== "OK" && r.category !== "ON_LEAVE"
          : r.category === filter)
      .filter((r) => !q || [r.clientCode, r.fileName, r.systemName, r.workingCompany]
        .some((v) => String(v ?? "").toLowerCase().includes(q)))
      .sort((a, b) => ORDER.indexOf(a.category) - ORDER.indexOf(b.category)
        || (a.rowNumber ?? 1e9) - (b.rowNumber ?? 1e9));
  }, [data, filter, query]);

  const isSelectable = (r) => rowActions(r, selectedCompany).some((a) => BULK.includes(a));
  const selectedRows = useMemo(
    () => (data ? data.results.filter((r) => selected.has(r._key)) : []),
    [data, selected],
  );
  // How many selected rows each bulk action applies to.
  const bulkCounts = useMemo(() => {
    const m = {};
    selectedRows.forEach((r) => rowActions(r, selectedCompany).forEach((a) => {
      if (BULK.includes(a)) m[a] = (m[a] || 0) + 1;
    }));
    return m;
  }, [selectedRows, selectedCompany]);

  const toggle = (key) => setSelected((prev) => {
    const next = new Set(prev);
    next.has(key) ? next.delete(key) : next.add(key);
    return next;
  });
  const shownSelectable = visible.slice(0, limit).filter(isSelectable);
  const allShownSelected = shownSelectable.length > 0 && shownSelectable.every((r) => selected.has(r._key));
  const toggleAllShown = () => setSelected((prev) => {
    const next = new Set(prev);
    if (allShownSelected) shownSelectable.forEach((r) => next.delete(r._key));
    else shownSelectable.forEach((r) => next.add(r._key));
    return next;
  });

  // ── Run an action on rows ────────────────────────────────────────────────
  const startAction = (action, rows) => {
    const eligible = rows.filter((r) => rowActions(r, selectedCompany).includes(action));
    if (!eligible.length) return;
    if (action === "edit") return onEditEmployee?.(eligible[0].infinityCode);
    if (action === "view") return onViewEmployee?.(eligible[0].infinityCode);
    if (action === "add") return onAddEmployee?.();
    if (action === "separate") {
      return setSep({
        rows: eligible, step: "form", actionType: "resignation", lastWorkingDay: todayIso(),
        resignationReason: "", reason: "", terminationType: "", validation: null, batch: null, err: "",
      });
    }
    let apiAction = action;
    let items;
    let preview = [];
    if (action === "apply") {
      apiAction = "applyFileValues";
      items = eligible.map((r) => ({
        infinityCode: r.infinityCode,
        changes: Object.fromEntries(r.diffs.filter((d) => d.applyKey).map((d) => [d.applyKey, d.file])),
      }));
      preview = eligible.slice(0, 8).map((r) =>
        `${r.clientCode}: ${r.diffs.filter((d) => d.applyKey).map((d) => `${d.label} "${d.system ?? "—"}" → "${d.file}"`).join(", ")}`);
    } else if (action === "move") {
      apiAction = "applyFileValues";
      items = eligible.map((r) => ({ infinityCode: r.infinityCode, changes: { workingCompany: selectedCompany } }));
    } else {
      items = eligible.map((r) => ({ infinityCode: r.infinityCode }));
    }
    setConfirm({ action, apiAction, items, n: items.length, preview });
  };

  const runConfirmed = async () => {
    if (!confirm) return;
    setBusy(true);
    try {
      const res = await apiFetch(`${BASE_URL}/employees/headcount/apply`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: confirm.apiAction, items: confirm.items }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body?.success) {
        throw new Error(body?.messages?.[ar ? "ar" : "en"] || body?.message || body?.error || `HTTP ${res.status}`);
      }
      setLastResult({
        ok: body.succeeded,
        bad: body.failed,
        failures: body.results.filter((r) => !r.ok).map((r) =>
          `${r.clientCode || "—"}: ${(ar && r.messageAr) || r.message || r.code}`),
      });
      setConfirm(null);
      await verify({ keepView: true });
    } catch (e) {
      setLastResult({ ok: 0, bad: confirm.n, failures: [e.message || String(e)] });
      setConfirm(null);
    } finally {
      setBusy(false);
    }
  };

  // ── Separation (Resign / Terminate) via the Bulk Actions engine ──────────
  const sepRows = () => sep.rows.map((r, i) => ({
    rowId: i + 1,
    rowNumber: i + 2,
    infinityCode: r.infinityCode,
    clientCode: r.clientCode || "",
    employeeName: r.systemName || "",
    actionType: sep.actionType,
    lastWorkingDay: sep.lastWorkingDay,
    reason: sep.reason,
    resignationReason: sep.actionType === "resignation" ? sep.resignationReason : "",
    terminationType: sep.actionType === "terminate" ? sep.terminationType : "",
  }));
  const bulkFetch = async (path, init = {}) => {
    const res = await apiFetch(`${BASE_URL}/employee-actions/bulk${path}`, {
      ...init,
      headers: { "x-view-type": "admin", ...(init.body ? { "Content-Type": "application/json" } : {}), ...(init.headers || {}) },
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok || body?.success === false) {
      throw new Error(body?.message || body?.messages?.[ar ? "ar" : "en"] || body?.error || `HTTP ${res.status}`);
    }
    return body.data;
  };
  const sepCheck = async () => {
    setSep((s) => ({ ...s, busy: true, err: "" }));
    try {
      const validation = await bulkFetch("/validate", { method: "POST", body: JSON.stringify({ rows: sepRows() }) });
      setSep((s) => ({ ...s, busy: false, step: "review", validation }));
    } catch (e) {
      setSep((s) => ({ ...s, busy: false, err: e.message }));
    }
  };
  const sepSubmit = async () => {
    setSep((s) => ({ ...s, busy: true, err: "", step: "running" }));
    try {
      const started = await bulkFetch("/commit", {
        method: "POST",
        body: JSON.stringify({ rows: sepRows(), fileName: `headcount-${data?.fileName || "verification"}`, clientBatchKey: newKey() }),
      });
      let batch = null;
      for (let i = 0; i < 400; i++) { // ≤ 10 min
        batch = await bulkFetch(`/batches/${started.batchId}`);
        setSep((s) => ({ ...s, batch }));
        if (batch.status !== "processing") break;
        await new Promise((r) => setTimeout(r, 1500));
      }
      setSep((s) => ({ ...s, busy: false, step: "done", batch }));
    } catch (e) {
      setSep((s) => ({ ...s, busy: false, step: "review", err: e.message }));
    }
  };
  const sepDone = async () => {
    setSep(null);
    await verify({ keepView: true });
  };

  const downloadReport = () => {
    const header = [t.row, t.result, t.code, t.name + ` (${t.inFile})`, t.name + ` (${t.system})`, t.companyCol, t.status, t.details];
    const sorted = [...data.results].sort((a, b) => ORDER.indexOf(a.category) - ORDER.indexOf(b.category) || (a.rowNumber ?? 1e9) - (b.rowNumber ?? 1e9));
    // NOTE: infinityCode is deliberately NOT exported to the file.
    const rows = sorted.map((r) => [
      r.rowNumber ?? "—",
      CATS[r.category][ar ? "ar" : "en"],
      r.clientCode ?? "",
      r.fileName ?? "",
      r.systemName ?? "",
      r.workingCompany ?? "",
      r.systemStatus ?? "",
      [
        ...(r.diffs || []).map((d) => `${d.label}: ${t.inFile} "${d.file}" / ${t.system} "${d.system ?? ""}"`),
        ...(r.issues || []).map((i) => issueText(i, ar)),
      ].join(" | "),
    ]);
    const ws = XLSX.utils.aoa_to_sheet([header, ...rows]);
    header.forEach((_, ci) => {
      const ref = XLSX.utils.encode_cell({ r: 0, c: ci });
      ws[ref].s = { font: { bold: true, color: { rgb: "FFFFFF" } }, fill: { fgColor: { rgb: "2E7CBA" } } };
    });
    sorted.forEach((r, ri) => {
      header.forEach((_, ci) => {
        const ref = XLSX.utils.encode_cell({ r: ri + 1, c: ci });
        if (ws[ref]) ws[ref].s = { fill: { fgColor: { rgb: CATS[r.category].fill } } };
      });
    });
    ws["!cols"] = [8, 26, 14, 24, 24, 20, 14, 70].map((w) => ({ wch: w }));
    if (ar) ws["!views"] = [{ RTL: true }];

    const s = data.summary;
    const summarySheet = XLSX.utils.aoa_to_sheet([
      [t.title],
      [t.scopeLabel(s)],
      [ar ? "الملف" : "File", data.fileName],
      [ar ? "وقت التحقق" : "Verified at", new Date(data.verifiedAt).toLocaleString()],
      [t.fileRows, s.fileRows],
      [t.activeInSystem, s.activeInSystem],
      ...ORDER.map((k) => [CATS[k][ar ? "ar" : "en"], counts[k] || 0]),
    ]);
    summarySheet["!cols"] = [{ wch: 32 }, { wch: 40 }];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, summarySheet, ar ? "ملخص" : "Summary");
    XLSX.utils.book_append_sheet(wb, ws, ar ? "النتائج" : "Results");
    const tag = (s.workingCompany || s.clientName || "client").replace(/[\\/:*?"<>|]+/g, "-");
    XLSX.writeFile(wb, `headcount-verification-${tag}-${data.verifiedAt.slice(0, 10)}.xlsx`);
  };

  if (!isOpen) return null;

  const canVerify = !!file && !loading && (scope === "client" || !!company);
  const s = data?.summary;
  const btn = (primary, disabled, extra = {}) => ({
    display: "inline-flex", alignItems: "center", gap: 6, padding: "9px 16px", borderRadius: 8,
    border: primary ? "none" : `1px solid ${c.border}`, cursor: disabled ? "not-allowed" : "pointer",
    background: primary ? c.accent : "transparent", color: primary ? "#fff" : c.text,
    opacity: disabled ? 0.55 : 1, fontWeight: 600, fontSize: 14, ...extra,
  });
  const miniBtn = (color) => ({
    display: "inline-flex", alignItems: "center", gap: 4, padding: "4px 8px", borderRadius: 6, fontSize: 12,
    fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap", border: `1px solid ${color}55`,
    background: `${color}14`, color,
  });
  const actColor = { activate: "#16a34a", restore: "#16a34a", apply: "#2563eb", move: "#c2410c", separate: "#dc2626", edit: c.dim, view: c.dim, add: "#2e7cba" };
  const inputStyle = { padding: "9px 11px", borderRadius: 8, border: `1px solid ${c.border}`, background: c.panel, color: c.text, fontSize: 14, width: "100%", boxSizing: "border-box" };
  const overlay = { position: "absolute", inset: 0, background: "rgba(2,6,23,0.45)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16, borderRadius: 14, zIndex: 5 };
  const card = { background: c.bg, border: `1px solid ${c.border}`, borderRadius: 12, padding: 20, width: "min(560px, 100%)", maxHeight: "85%", overflow: "auto", boxShadow: "0 20px 50px rgba(0,0,0,0.3)" };

  return (
    <div
      onClick={() => { if (!busy && !sep?.busy) close(); }}
      style={{ position: "fixed", inset: 0, background: "rgba(2,6,23,0.55)", zIndex: 2000, display: hidden ? "none" : "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
    >
      <div
        role="dialog"
        aria-modal="true"
        dir={ar ? "rtl" : "ltr"}
        onClick={(e) => e.stopPropagation()}
        style={{ position: "relative", background: c.bg, color: c.text, borderRadius: 14, width: data ? "min(1280px, 100%)" : "min(620px, 100%)", maxHeight: "92vh", display: "flex", flexDirection: "column", boxShadow: "0 25px 60px rgba(0,0,0,0.35)", border: `1px solid ${c.border}` }}
      >
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 20px", borderBottom: `1px solid ${c.border}` }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <ShieldCheck size={22} color={c.accent} />
            <div>
              <div style={{ fontSize: 18, fontWeight: 700 }}>{t.title}</div>
              {s && <div style={{ fontSize: 13, color: c.dim }}>{t.scopeLabel(s)} · {data.fileName}</div>}
            </div>
          </div>
          <button onClick={close} aria-label="close" style={{ background: "none", border: "none", cursor: "pointer", color: c.dim }}><X size={20} /></button>
        </div>

        {/* Step 1: setup */}
        {!data && (
          <div style={{ padding: 20, display: "flex", flexDirection: "column", gap: 16 }}>
            <div style={{ fontSize: 14, color: c.dim, lineHeight: 1.6 }}>{t.subtitle}</div>

            <div>
              <div style={{ fontWeight: 600, marginBottom: 8 }}>{t.compareAgainst}</div>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                {[["company", t.company], ["client", t.wholeClient]].map(([k, label]) => (
                  <label key={k} style={{ flex: "1 1 200px", display: "flex", alignItems: "center", gap: 8, padding: "10px 12px", borderRadius: 8, cursor: "pointer", border: `1.5px solid ${scope === k ? c.accent : c.border}`, background: scope === k ? (dark ? "rgba(46,124,186,0.15)" : "rgba(46,124,186,0.07)") : "transparent" }}>
                    <input type="radio" name="hc-scope" checked={scope === k} onChange={() => { setScope(k); resetResult(); }} />
                    {label}
                  </label>
                ))}
              </div>
            </div>

            {scope === "company" && (
              <select value={company} onChange={(e) => setCompany(e.target.value)} style={inputStyle}>
                <option value="">{t.chooseCompany}</option>
                {companyOptions.map((name) => <option key={name} value={name}>{name}</option>)}
              </select>
            )}

            <div>
              <div style={{ fontWeight: 600, marginBottom: 8 }}>{t.file}</div>
              <input
                ref={fileRef}
                type="file"
                accept=".xlsx,.xls,.xlsm,.csv,.ods"
                style={{ display: "none" }}
                onChange={(e) => { setFile(e.target.files?.[0] || null); setError(""); }}
              />
              <button onClick={() => fileRef.current?.click()} style={{ ...btn(false, false), width: "100%", justifyContent: "center", borderStyle: "dashed", padding: 16 }}>
                {file ? <FileSpreadsheet size={18} color="#16a34a" /> : <Upload size={18} />}
                {file ? file.name : t.pick}
              </button>
              <div style={{ fontSize: 12, color: c.dim, marginTop: 8, lineHeight: 1.6 }}>{t.hint}</div>
            </div>

            {error && <div style={{ background: dark ? "rgba(220,38,38,0.15)" : "#fef2f2", color: "#dc2626", padding: "10px 12px", borderRadius: 8, fontSize: 14 }}>{error}</div>}

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button onClick={close} style={btn(false, false)}>{t.cancel}</button>
              <button onClick={() => verify()} disabled={!canVerify} style={btn(true, !canVerify)}>
                <ShieldCheck size={16} /> {loading ? t.verifying : t.verify}
              </button>
            </div>
          </div>
        )}

        {/* Step 2: results */}
        {data && (
          <div style={{ padding: 20, display: "flex", flexDirection: "column", gap: 12, minHeight: 0, flex: 1 }}>
            <div style={{ padding: "12px 14px", borderRadius: 10, fontWeight: 700, background: s.allMatch ? (dark ? "rgba(22,163,74,0.15)" : "#ecfdf5") : (dark ? "rgba(220,38,38,0.15)" : "#fef2f2"), color: s.allMatch ? "#16a34a" : "#dc2626" }}>
              {s.allMatch ? t.allGood(s.fileRows) : t.notGood(s.issues)}
              <div style={{ fontWeight: 400, fontSize: 13, color: c.dim, marginTop: 4 }}>
                {t.fileRows}: <b>{s.fileRows}</b> · {t.activeInSystem}: <b>{s.activeInSystem}</b> · {t.compared}: {s.comparedFields.length ? s.comparedFields.join(", ") : t.none}
                {loading && <> · <Loader2 size={12} style={{ verticalAlign: "middle" }} /> {t.reverifying}</>}
              </div>
            </div>

            {lastResult && (
              <div style={{ padding: "10px 14px", borderRadius: 10, fontSize: 13, display: "flex", justifyContent: "space-between", gap: 10, background: lastResult.bad ? (dark ? "rgba(217,119,6,0.15)" : "#fffbeb") : (dark ? "rgba(22,163,74,0.15)" : "#ecfdf5") }}>
                <div>
                  <b style={{ color: lastResult.bad ? "#d97706" : "#16a34a" }}>{t.resultTitle(lastResult.ok, lastResult.bad)}</b>
                  {lastResult.failures.slice(0, 6).map((f, i) => <div key={i} style={{ color: c.dim }}>{f}</div>)}
                  {lastResult.failures.length > 6 && <div style={{ color: c.dim }}>… +{lastResult.failures.length - 6}</div>}
                </div>
                <button onClick={() => setLastResult(null)} style={{ background: "none", border: "none", cursor: "pointer", color: c.dim, alignSelf: "flex-start" }}><X size={16} /></button>
              </div>
            )}

            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {[["ISSUES", t.issues, s.issues, "#dc2626"], ...ORDER.filter((k) => counts[k]).map((k) => [k, CATS[k][ar ? "ar" : "en"], counts[k], CATS[k].color]), ["ALL", t.everything, data.results.length, c.dim]]
                .map(([k, label, n, color]) => (
                  <button key={k} onClick={() => { setFilter(k); setLimit(PAGE); }}
                    style={{ padding: "5px 11px", borderRadius: 999, fontSize: 13, cursor: "pointer", color: c.text, background: filter === k ? (dark ? "rgba(255,255,255,0.1)" : "rgba(15,23,42,0.06)") : "transparent", border: `1.5px solid ${filter === k ? color : c.border}` }}>
                    <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: 4, background: color, marginInlineEnd: 6 }} />
                    {label} <b>{n}</b>
                  </button>
                ))}
            </div>

            <div style={{ position: "relative" }}>
              <Search size={16} color={c.dim} style={{ position: "absolute", top: 11, insetInlineStart: 10 }} />
              <input value={query} onChange={(e) => { setQuery(e.target.value); setLimit(PAGE); }} placeholder={t.search}
                style={{ ...inputStyle, paddingInlineStart: 34 }} />
            </div>

            {/* Bulk action bar */}
            {selected.size > 0 && (
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", padding: "8px 12px", borderRadius: 10, background: dark ? "rgba(46,124,186,0.15)" : "rgba(46,124,186,0.08)", border: `1px solid ${c.accent}55` }}>
                <b style={{ fontSize: 13 }}>{t.selectedN(selected.size)}</b>
                {BULK.filter((a) => bulkCounts[a]).map((a) => {
                  const Icon = ACT_ICON[a];
                  return (
                    <button key={a} disabled={busy || loading} onClick={() => startAction(a, selectedRows)} style={miniBtn(actColor[a])}>
                      <Icon size={13} /> {a === "move" ? `${t.act.move}: ${selectedCompany}` : t.act[a]} ({bulkCounts[a]})
                    </button>
                  );
                })}
                <button onClick={() => setSelected(new Set())} style={{ ...miniBtn(c.dim), marginInlineStart: "auto" }}>{t.clear}</button>
              </div>
            )}

            <div style={{ overflow: "auto", border: `1px solid ${c.border}`, borderRadius: 10, flex: 1, minHeight: 220 }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr>
                    <th style={{ position: "sticky", top: 0, background: c.panel, padding: "9px 10px", borderBottom: `1px solid ${c.border}`, zIndex: 1, width: 32 }}>
                      <input type="checkbox" title={t.selectAll} checked={allShownSelected} disabled={!shownSelectable.length} onChange={toggleAllShown} />
                    </th>
                    {[t.row, t.code, t.name, t.companyCol, t.status, t.result, t.details, t.actions].map((h) => (
                      <th key={h} style={{ position: "sticky", top: 0, background: c.panel, textAlign: "start", padding: "9px 10px", borderBottom: `1px solid ${c.border}`, whiteSpace: "nowrap", zIndex: 1 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {visible.slice(0, limit).map((r) => {
                    const cat = CATS[r.category];
                    const tint = `${cat.color}${dark ? "26" : "14"}`;
                    const acts = rowActions(r, selectedCompany);
                    return (
                      <tr key={r._key} style={{ background: r.category === "OK" ? "transparent" : tint, borderInlineStart: `4px solid ${cat.color}` }}>
                        <td style={{ padding: "8px 10px" }}>
                          {isSelectable(r) && <input type="checkbox" checked={selected.has(r._key)} onChange={() => toggle(r._key)} />}
                        </td>
                        <td style={{ padding: "8px 10px", color: c.dim }}>{r.rowNumber ?? "—"}</td>
                        <td style={{ padding: "8px 10px", fontFamily: "ui-monospace, monospace" }}>{r.clientCode ?? "—"}</td>
                        <td style={{ padding: "8px 10px" }}>
                          {r.systemName ?? r.fileName ?? "—"}
                          {r.fileName && r.systemName && r.fileName !== r.systemName && (
                            <div style={{ fontSize: 12, color: c.dim }}>{t.inFile}: {r.fileName}</div>
                          )}
                        </td>
                        <td style={{ padding: "8px 10px" }}>{r.workingCompany ?? "—"}</td>
                        <td style={{ padding: "8px 10px" }}>{r.systemStatus ?? "—"}</td>
                        <td style={{ padding: "8px 10px" }}>
                          <span style={{ whiteSpace: "nowrap", padding: "2px 9px", borderRadius: 999, fontSize: 12, fontWeight: 600, color: cat.color, background: `${cat.color}22` }}>
                            {cat[ar ? "ar" : "en"]}
                          </span>
                        </td>
                        <td style={{ padding: "8px 10px", lineHeight: 1.6 }}>
                          {(r.diffs || []).map((d) => (
                            <div key={d.field}>
                              <b>{d.label}</b>: <span style={{ color: "#dc2626", textDecoration: "line-through" }}>{String(d.system ?? "—")}</span>
                              {" → "}<span style={{ color: "#16a34a", fontWeight: 600 }}>{String(d.file)}</span>
                              <span style={{ color: c.dim, fontSize: 11 }}> ({t.system} → {t.inFile})</span>
                            </div>
                          ))}
                          {(r.issues || []).map((iss, k) => <div key={k} style={{ color: c.dim }}>{issueText(iss, ar)}</div>)}
                        </td>
                        <td style={{ padding: "8px 10px" }}>
                          <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                            {acts.map((a) => {
                              const Icon = ACT_ICON[a];
                              return (
                                <button key={a} disabled={busy || loading} onClick={() => startAction(a, [r])} style={miniBtn(actColor[a])}
                                  title={a === "move" ? `${t.act.move}: ${selectedCompany}` : t.act[a]}>
                                  <Icon size={13} /> {t.act[a]}
                                </button>
                              );
                            })}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {!visible.length && (
                    <tr><td colSpan={9} style={{ padding: 28, textAlign: "center", color: c.dim }}>{t.nothing}</td></tr>
                  )}
                </tbody>
              </table>
              {visible.length > limit && (
                <div style={{ padding: 12, textAlign: "center" }}>
                  <button onClick={() => setLimit((l) => l + PAGE)} style={btn(false, false)}>{t.showMore} ({visible.length - limit})</button>
                </div>
              )}
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, flexWrap: "wrap" }}>
              <button onClick={resetAll} style={btn(false, false)}><RotateCcw size={16} /> {t.again}</button>
              <button onClick={downloadReport} style={btn(true, false)}><Download size={16} /> {t.download}</button>
            </div>
          </div>
        )}

        {/* Confirm dialog (activate / restore / apply / move) */}
        {confirm && (
          <div style={overlay} onClick={() => !busy && setConfirm(null)}>
            <div style={card} onClick={(e) => e.stopPropagation()}>
              <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>
                {confirm.action === "move"
                  ? t.confirmTitle.move(confirm.n, selectedCompany)
                  : t.confirmTitle[confirm.apiAction](confirm.n)}
              </div>
              <div style={{ fontSize: 13, color: c.dim, lineHeight: 1.6, marginBottom: 10 }}>
                {t.confirmBody[confirm.action === "move" ? "move" : confirm.apiAction]}
              </div>
              {confirm.preview.length > 0 && (
                <div style={{ fontSize: 12, background: c.panel, borderRadius: 8, padding: 10, marginBottom: 10, lineHeight: 1.7 }}>
                  {confirm.preview.map((p, i) => <div key={i}>{p}</div>)}
                  {confirm.n > confirm.preview.length && <div style={{ color: c.dim }}>… +{confirm.n - confirm.preview.length}</div>}
                </div>
              )}
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                <button disabled={busy} onClick={() => setConfirm(null)} style={btn(false, busy)}>{t.cancel}</button>
                <button disabled={busy} onClick={runConfirmed} style={btn(true, busy, { background: actColor[confirm.action] })}>
                  {busy ? t.working : t.confirm}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Separation dialog (Resign / Terminate via Bulk Actions) */}
        {sep && (
          <div style={overlay} onClick={() => !sep.busy && sep.step !== "running" && setSep(null)}>
            <div style={card} onClick={(e) => e.stopPropagation()}>
              <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 6 }}>{t.sep.title(sep.rows.length)}</div>
              <div style={{ fontSize: 12, color: c.dim, lineHeight: 1.6, marginBottom: 12 }}>{t.sep.hint}</div>

              {sep.step === "form" && (
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  <div style={{ display: "flex", gap: 8 }}>
                    {["resignation", "terminate"].map((k) => (
                      <label key={k} style={{ flex: 1, display: "flex", gap: 8, alignItems: "center", padding: "9px 11px", borderRadius: 8, cursor: "pointer", border: `1.5px solid ${sep.actionType === k ? "#dc2626" : c.border}` }}>
                        <input type="radio" checked={sep.actionType === k} onChange={() => setSep((x) => ({ ...x, actionType: k }))} />
                        {t.sep[k]}
                      </label>
                    ))}
                  </div>
                  <label style={{ fontSize: 13, fontWeight: 600 }}>{t.sep.lwd}
                    <input type="date" value={sep.lastWorkingDay} onChange={(e) => setSep((x) => ({ ...x, lastWorkingDay: e.target.value }))} style={{ ...inputStyle, marginTop: 4 }} />
                  </label>
                  {sep.actionType === "resignation" ? (
                    <label style={{ fontSize: 13, fontWeight: 600 }}>{t.sep.reason}
                      <select value={sep.resignationReason} onChange={(e) => setSep((x) => ({ ...x, resignationReason: e.target.value }))} style={{ ...inputStyle, marginTop: 4 }}>
                        <option value="">—</option>
                        {Object.entries(bt.resignationReasons || {}).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                      </select>
                    </label>
                  ) : (
                    <label style={{ fontSize: 13, fontWeight: 600 }}>{t.sep.termType}
                      <select value={sep.terminationType} onChange={(e) => setSep((x) => ({ ...x, terminationType: e.target.value }))} style={{ ...inputStyle, marginTop: 4 }}>
                        <option value="">—</option>
                        {Object.entries(bt.terminationTypes || {}).filter(([k]) => k !== "voluntary").map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                      </select>
                    </label>
                  )}
                  <label style={{ fontSize: 13, fontWeight: 600 }}>{sep.actionType === "resignation" ? t.sep.details : t.sep.reason}
                    <textarea rows={2} value={sep.reason} onChange={(e) => setSep((x) => ({ ...x, reason: e.target.value }))} style={{ ...inputStyle, marginTop: 4, resize: "vertical" }} />
                  </label>
                </div>
              )}

              {(sep.step === "review" || sep.step === "running" || sep.step === "done") && sep.validation && (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", fontSize: 13 }}>
                    <span style={miniBtn("#16a34a")}>{t.sep.valid}: {sep.validation.totals.valid}</span>
                    <span style={miniBtn("#d97706")}>{t.sep.warning}: {sep.validation.totals.warning}</span>
                    <span style={miniBtn("#dc2626")}>{t.sep.error}: {sep.validation.totals.error}</span>
                  </div>
                  <div style={{ maxHeight: 220, overflow: "auto", fontSize: 12, background: c.panel, borderRadius: 8, padding: 10, lineHeight: 1.7 }}>
                    {sep.validation.rows.filter((r) => r.messages.length).map((r) => (
                      <div key={r.rowId} style={{ color: r.status === "error" ? "#dc2626" : "#d97706" }}>
                        <b>{r.clientCode || r.employee?.clientCode || "—"}</b>: {r.messages.map((m) => translateBulkMessage(bt, m)).join(" · ")}
                      </div>
                    ))}
                    {!sep.validation.rows.some((r) => r.messages.length) && <div style={{ color: "#16a34a" }}>✓</div>}
                  </div>
                </div>
              )}

              {(sep.step === "running" || sep.step === "done") && sep.batch && (
                <div style={{ marginTop: 10, fontSize: 13 }}>
                  <b>{sep.step === "running" ? t.sep.running : t.sep.finished}</b> — {sep.batch.processedCount}/{sep.batch.queuedRows}
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 6 }}>
                    {Object.entries(sep.batch.byOutcome || {}).map(([k, n]) => (
                      <span key={k} style={miniBtn(k === "failed" || k === "skipped" ? "#dc2626" : "#16a34a")}>{bt.outcomes?.[k] || k}: {n}</span>
                    ))}
                  </div>
                </div>
              )}
              {sep.step === "running" && !sep.batch && <div style={{ marginTop: 10, fontSize: 13 }}><Loader2 size={14} /> {t.sep.running}</div>}

              {sep.err && <div style={{ marginTop: 10, color: "#dc2626", fontSize: 13 }}>{sep.err}</div>}

              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 14 }}>
                {sep.step === "form" && <>
                  <button onClick={() => setSep(null)} disabled={sep.busy} style={btn(false, sep.busy)}>{t.cancel}</button>
                  <button onClick={sepCheck} disabled={sep.busy || !sep.lastWorkingDay} style={btn(true, sep.busy || !sep.lastWorkingDay)}>{sep.busy ? t.working : t.sep.check}</button>
                </>}
                {sep.step === "review" && <>
                  <button onClick={() => setSep((x) => ({ ...x, step: "form", validation: null, err: "" }))} disabled={sep.busy} style={btn(false, sep.busy)}>{t.sep.back}</button>
                  <button onClick={sepSubmit}
                    disabled={sep.busy || (sep.validation.totals.valid + sep.validation.totals.warning) === 0}
                    style={btn(true, sep.busy || (sep.validation.totals.valid + sep.validation.totals.warning) === 0, { background: "#dc2626" })}>
                    {t.sep.submit(sep.validation.totals.valid + sep.validation.totals.warning)}
                  </button>
                </>}
                {sep.step === "done" && <button onClick={sepDone} style={btn(true, false)}>{t.sep.done}</button>}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
